/**
 * Cadastro e edição de fornecedor — PF e PJ na mesma tela.
 *
 * Mesmo desenho de `FormularioCliente.tsx`, com os campos que só fazem
 * sentido do lado de quem COMPRA removidos: sem preferência de grade
 * (tamanho/numeração/cor), sem crediário e sem os dois consentimentos de
 * marketing da LGPD. Fornecedor não é titular de dado pessoal de consumo.
 *
 * Diferente do cliente, nome fantasia e inscrição estadual aparecem nos dois
 * tipos: a migração 0010 não restringe esses campos a pessoa jurídica, e um
 * fornecedor autônomo (a costureira, o serigrafista que a migração cita) pode
 * vender sob nome de fantasia e ter inscrição de produtor.
 */
import { useEffect, useState, type ReactNode } from 'react'

import { Segmentado } from '../../componentes/Segmentado'
import { Campo, MensagemDeErro } from '../../componentes/Campos'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { buscarCEP, buscarCNPJ } from '../../plataforma/auxiliares'
import {
  UFS,
  criarFornecedor,
  salvarFornecedor,
  type FichaFornecedor,
  type PedidoFornecedor,
  type StatusFornecedor,
  type TipoFornecedor,
} from '../../plataforma/fornecedores'
import {
  cepCompleto,
  cnpjValido,
  cpfValido,
  limparCEP,
  limparCNPJ,
  limparCPF,
  limparTelefone,
  mascararCEP,
  mascararCNPJ,
  mascararCPF,
  mascararTelefone,
  telefoneCompleto,
} from '../../plataforma/mascaras'

type Rascunho = {
  tipo: TipoFornecedor
  nome: string
  nomeFantasia: string
  documento: string
  inscricaoEstadual: string
  telefone: string
  email: string
  cep: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  uf: string
  prazoPagamentoDias: string
  observacao: string
  status: StatusFornecedor
}

const VAZIO: Rascunho = {
  tipo: 'pf',
  nome: '',
  nomeFantasia: '',
  documento: '',
  inscricaoEstadual: '',
  telefone: '',
  email: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  prazoPagamentoDias: '',
  observacao: '',
  status: 'ativo',
}

function rascunhoDe(ficha: FichaFornecedor): Rascunho {
  return {
    tipo: ficha.tipo,
    nome: ficha.nome,
    nomeFantasia: ficha.nome_fantasia ?? '',
    documento: ficha.documento ?? '',
    inscricaoEstadual: ficha.inscricao_estadual ?? '',
    telefone: ficha.contato.telefone ?? '',
    email: ficha.contato.email ?? '',
    cep: ficha.endereco.cep ?? '',
    logradouro: ficha.endereco.logradouro ?? '',
    numero: ficha.endereco.numero ?? '',
    complemento: ficha.endereco.complemento ?? '',
    bairro: ficha.endereco.bairro ?? '',
    cidade: ficha.endereco.cidade ?? '',
    uf: ficha.endereco.uf ?? '',
    prazoPagamentoDias:
      ficha.prazo_pagamento_dias !== undefined ? String(ficha.prazo_pagamento_dias) : '',
    observacao: ficha.observacao_interna ?? '',
    status: ficha.status,
  }
}

/** Erro por campo, no mesmo formato que a API devolve em `erro.campos`. */
type Erros = Record<string, string>

/**
 * Confere o que dá para conferir sem ir ao servidor.
 *
 * Não substitui a validação do backend — duplica de propósito o que faz o erro
 * aparecer no campo em que a pessoa digitou.
 */
function conferir(rascunho: Rascunho): Erros {
  const erros: Erros = {}
  const pf = rascunho.tipo === 'pf'

  if (rascunho.nome.trim().length < 2) {
    erros.nome = pf ? 'Informe o nome completo do fornecedor.' : 'Informe a razão social.'
  }

  const documento = pf ? limparCPF(rascunho.documento) : limparCNPJ(rascunho.documento)
  if (!pf && documento === '') {
    erros.documento = 'Pessoa jurídica exige CNPJ.'
  } else if (documento !== '') {
    const valido = pf ? cpfValido(documento) : cnpjValido(documento)
    if (!valido) erros.documento = pf ? 'CPF inválido. Confira os dígitos.' : 'CNPJ inválido. Confira os dígitos.'
  }

  if (rascunho.telefone !== '' && !telefoneCompleto(rascunho.telefone)) {
    erros.telefone = 'Telefone com DDD: 10 dígitos no fixo, 11 no celular.'
  }
  if (rascunho.email !== '' && !/^[^\s@]+@[^\s@]+$/.test(rascunho.email.trim())) {
    erros.email = 'E-mail inválido.'
  }
  if (rascunho.cep !== '' && !cepCompleto(rascunho.cep)) {
    erros.cep = 'CEP tem 8 dígitos.'
  }

  if (rascunho.prazoPagamentoDias !== '') {
    const prazo = Number(rascunho.prazoPagamentoDias)
    if (!Number.isInteger(prazo) || prazo < 0 || prazo > 180) {
      erros.prazo_pagamento_dias = 'O prazo de pagamento vai de 0 a 180 dias.'
    }
  }

  return erros
}

/** Monta o corpo do pedido. */
function pedidoDe(rascunho: Rascunho): PedidoFornecedor {
  const pf = rascunho.tipo === 'pf'
  const semVazio = (texto: string) => (texto.trim() === '' ? undefined : texto.trim())

  return {
    tipo: rascunho.tipo,
    nome: rascunho.nome.trim(),
    nome_fantasia: semVazio(rascunho.nomeFantasia),
    inscricao_estadual: semVazio(rascunho.inscricaoEstadual),
    documento: (pf ? limparCPF(rascunho.documento) : limparCNPJ(rascunho.documento)) || undefined,
    email: semVazio(rascunho.email.toLowerCase()),
    telefone: limparTelefone(rascunho.telefone) || undefined,
    cep: limparCEP(rascunho.cep) || undefined,
    logradouro: semVazio(rascunho.logradouro),
    numero: semVazio(rascunho.numero),
    complemento: semVazio(rascunho.complemento),
    bairro: semVazio(rascunho.bairro),
    cidade: semVazio(rascunho.cidade),
    uf: semVazio(rascunho.uf),
    // Ausente é "a combinar por pedido" (migração 0010) — não é zero.
    prazo_pagamento_dias:
      rascunho.prazoPagamentoDias === '' ? undefined : Number(rascunho.prazoPagamentoDias),
    observacao_interna: semVazio(rascunho.observacao),
    status: rascunho.status,
  }
}

/** Ver `ID_FORMULARIO_CLIENTE` em `FormularioCliente.tsx` — mesmo motivo. */
export const ID_FORMULARIO_FORNECEDOR = 'formulario-fornecedor'

export function FormularioFornecedor({
  ficha,
  aoSalvar,
  aoEstadoMudar,
}: {
  /** Ficha existente em modo edição; ausente em cadastro novo. */
  ficha?: FichaFornecedor
  aoSalvar: (salva: FichaFornecedor) => void
  /** Avisa o cabeçalho (dono dos botões "Cancelar"/"Salvar") se está enviando agora. */
  aoEstadoMudar?: (enviando: boolean) => void
}) {
  const [rascunho, setRascunho] = useState<Rascunho>(ficha ? rascunhoDe(ficha) : VAZIO)
  const [erros, setErros] = useState<Erros>({})
  const [aviso, setAviso] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    aoEstadoMudar?.(enviando)
  }, [enviando, aoEstadoMudar])

  const [buscandoCEP, setBuscandoCEP] = useState(false)
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false)

  const pf = rascunho.tipo === 'pf'
  const alterar = <C extends keyof Rascunho>(campo: C, valor: Rascunho[C]) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }))

  /** Mesmo comportamento de `FormularioCliente.tsx`: CEP completo SUBSTITUI o endereço. */
  const aoMudarCEP = (texto: string) => {
    alterar('cep', texto)
    if (limparCEP(texto).length !== 8) return

    setBuscandoCEP(true)
    buscarCEP(texto)
      .then((endereco) => {
        if (!endereco) return
        setRascunho((atual) => ({
          ...atual,
          logradouro: endereco.logradouro || atual.logradouro,
          bairro: endereco.bairro || atual.bairro,
          cidade: endereco.cidade || atual.cidade,
          uf: endereco.uf || atual.uf,
        }))
      })
      .finally(() => setBuscandoCEP(false))
  }

  /**
   * Mesmo comportamento de `FormularioCliente.tsx`: CNPJ completo só
   * preenche campo VAZIO — nunca substitui o que a pessoa já digitou.
   */
  const aoMudarDocumento = (texto: string) => {
    alterar('documento', texto)
    if (pf || limparCNPJ(texto).length !== 14) return

    setBuscandoCNPJ(true)
    buscarCNPJ(texto)
      .then((dados) => {
        if (!dados) return
        setRascunho((atual) => ({
          ...atual,
          nome: atual.nome.trim() === '' ? dados.razao_social : atual.nome,
          nomeFantasia: atual.nomeFantasia.trim() === '' ? dados.nome_fantasia : atual.nomeFantasia,
          telefone: atual.telefone.trim() === '' ? (dados.telefone ?? '') : atual.telefone,
          email: atual.email.trim() === '' ? (dados.email ?? '') : atual.email,
          cep: atual.cep.trim() === '' ? dados.cep : atual.cep,
          logradouro: atual.logradouro.trim() === '' ? dados.logradouro : atual.logradouro,
          numero: atual.numero.trim() === '' ? dados.numero : atual.numero,
          complemento: atual.complemento.trim() === '' ? dados.complemento : atual.complemento,
          bairro: atual.bairro.trim() === '' ? dados.bairro : atual.bairro,
          cidade: atual.cidade.trim() === '' ? dados.cidade : atual.cidade,
          uf: atual.uf.trim() === '' ? dados.uf : atual.uf,
        }))
      })
      .finally(() => setBuscandoCNPJ(false))
  }

  async function enviar() {
    const encontrados = conferir(rascunho)
    if (Object.keys(encontrados).length > 0) {
      setErros(encontrados)
      setAviso('Confira os campos destacados.')
      return
    }

    setErros({})
    setAviso('')
    setEnviando(true)
    try {
      const pedido = pedidoDe(rascunho)
      const salva = ficha
        ? await salvarFornecedor(ficha.codigo, pedido)
        : await criarFornecedor(pedido)
      aoSalvar(salva)
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível salvar o fornecedor.')
        return
      }
      // A API devolve erro POR CAMPO — é isso que permite destacar o input em
      // vez de mostrar um aviso solto no topo do formulário.
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <form
      id={ID_FORMULARIO_FORNECEDOR}
      onSubmit={(evento) => {
        evento.preventDefault()
        void enviar()
      }}
      className="grid w-full items-start gap-4 xl:grid-cols-[minmax(0,1fr)_300px]"
    >
      {/* Full-width, logo abaixo do cabeçalho — ver FormularioCliente.tsx. */}
      {aviso && (
        <div
          role="alert"
          className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto xl:col-span-2"
        >
          {aviso}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <Cartao
          titulo="Identificação"
          subtitulo="Dados usados no pedido de compra e na ficha do fornecedor"
        >
          <div className="flex flex-col gap-4">
            <div>
              <span className="mb-[7px] block text-[13px] font-semibold text-texto">
                Tipo de fornecedor
              </span>
              {ficha ? (
                // Mesma regra de FormularioCliente.tsx: trocar o tipo depois de
                // criado limparia o documento, e na edição isso é perda de
                // dado por engano, não uma correção.
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex items-center rounded-[6px] bg-marca-suave px-3 py-2 text-[13.5px] font-semibold text-marca-forte">
                    {pf ? 'Pessoa física' : 'Pessoa jurídica'}
                  </span>
                  <span className="text-xs text-texto-suave">
                    Não muda depois de criado — cadastre um fornecedor novo se for o caso.
                  </span>
                </div>
              ) : (
                <Segmentado
                  opcoes={[
                    { chave: 'pf', rotulo: 'Pessoa física' },
                    { chave: 'pj', rotulo: 'Pessoa jurídica' },
                  ]}
                  valor={rascunho.tipo}
                  aoTrocar={(valor) => {
                    // Trocar o tipo limpa o documento: um CPF de 11 dígitos não
                    // é um CNPJ, e manter o texto faria a máscara mostrar um
                    // número que a API vai recusar.
                    setRascunho((atual) => ({ ...atual, tipo: valor, documento: '' }))
                    setErros({})
                  }}
                />
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo={pf ? 'CPF' : 'CNPJ'}
                inputMode={pf ? 'numeric' : 'text'}
                value={pf ? mascararCPF(rascunho.documento) : mascararCNPJ(rascunho.documento)}
                onChange={(evento) => aoMudarDocumento(evento.target.value)}
                placeholder={pf ? '000.000.000-00' : '00.000.000/0000-00'}
                erro={erros.documento}
                acessorio={buscandoCNPJ ? <IndicadorBusca /> : undefined}
                dica={
                  pf
                    ? 'Opcional no fornecedor autônomo'
                    : 'Obrigatório na nota de entrada — preenche o resto sozinho'
                }
              />

              <Campo
                rotulo="Inscrição estadual"
                value={rascunho.inscricaoEstadual}
                onChange={(evento) => alterar('inscricaoEstadual', evento.target.value)}
                placeholder="Isento"
                erro={erros.inscricao_estadual}
              />
            </div>

            <Campo
              rotulo={pf ? 'Nome completo' : 'Razão social'}
              value={rascunho.nome}
              onChange={(evento) => alterar('nome', evento.target.value)}
              placeholder={pf ? 'Marcos Iwata' : 'RAD Equipamentos Esportivos Ltda'}
              erro={erros.nome}
            />

            <Campo
              rotulo="Nome fantasia"
              value={rascunho.nomeFantasia}
              onChange={(evento) => alterar('nomeFantasia', evento.target.value)}
              placeholder="RAD Brasil"
              erro={erros.nome_fantasia}
            />
          </div>
        </Cartao>

        <Cartao titulo="Contato" subtitulo="Quem a loja fala ao fazer um pedido">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Telefone"
              inputMode="numeric"
              value={mascararTelefone(rascunho.telefone)}
              onChange={(evento) => alterar('telefone', evento.target.value)}
              placeholder="(11) 90000-0000"
              erro={erros.telefone}
            />
            <Campo
              rotulo="E-mail de pedidos"
              type="email"
              value={rascunho.email}
              onChange={(evento) => alterar('email', evento.target.value)}
              placeholder="comercial@fornecedor.com.br"
              erro={erros.email}
            />
          </div>
        </Cartao>

        <Cartao titulo="Endereço" subtitulo="Necessário para recebimento e para a nota de entrada">
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Campo
                rotulo="CEP"
                inputMode="numeric"
                value={mascararCEP(rascunho.cep)}
                onChange={(evento) => aoMudarCEP(evento.target.value)}
                placeholder="00000-000"
                erro={erros.cep}
                acessorio={buscandoCEP ? <IndicadorBusca /> : undefined}
              />
              <Campo
                rotulo="Número"
                inputMode="numeric"
                value={rascunho.numero}
                onChange={(evento) => alterar('numero', evento.target.value)}
                placeholder="000"
                erro={erros.numero}
              />
              <Campo
                rotulo="Complemento"
                value={rascunho.complemento}
                onChange={(evento) => alterar('complemento', evento.target.value)}
                placeholder="Galpão, bloco"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Rua"
                value={rascunho.logradouro}
                onChange={(evento) => alterar('logradouro', evento.target.value)}
                placeholder="Av. Industrial"
                erro={erros.logradouro}
              />
              <Campo
                rotulo="Bairro"
                value={rascunho.bairro}
                onChange={(evento) => alterar('bairro', evento.target.value)}
                placeholder="Distrito Industrial"
                erro={erros.bairro}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Cidade"
                value={rascunho.cidade}
                onChange={(evento) => alterar('cidade', evento.target.value)}
                placeholder="Curitiba"
                erro={erros.cidade}
              />
              <div>
                <label
                  htmlFor="fornecedor-uf"
                  className="mb-2 block text-[13px] font-semibold text-texto"
                >
                  UF
                </label>
                <select
                  id="fornecedor-uf"
                  className="campo"
                  aria-invalid={erros.uf ? true : undefined}
                  value={rascunho.uf}
                  onChange={(evento) => alterar('uf', evento.target.value)}
                >
                  <option value="">Selecione</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
                {erros.uf && <MensagemDeErro>{erros.uf}</MensagemDeErro>}
              </div>
            </div>
          </div>
        </Cartao>

        <Cartao
          titulo="Condições comerciais"
          subtitulo="O prazo de pagamento combinado com este fornecedor"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Prazo de pagamento (dias)"
              inputMode="numeric"
              value={rascunho.prazoPagamentoDias}
              onChange={(evento) =>
                alterar('prazoPagamentoDias', evento.target.value.replace(/[^0-9]/g, ''))
              }
              placeholder="30"
              erro={erros.prazo_pagamento_dias}
              dica="Em branco fica a combinar a cada pedido"
            />
          </div>
        </Cartao>

        <Cartao titulo="Observações" subtitulo="Visível só para a equipe">
          <div className="flex flex-col gap-[18px]">
            <div>
              <label
                htmlFor="fornecedor-observacao"
                className="mb-2 block text-[13px] font-semibold text-texto"
              >
                Observações internas
              </label>
              <textarea
                id="fornecedor-observacao"
                value={rascunho.observacao}
                onChange={(evento) => alterar('observacao', evento.target.value.slice(0, 1000))}
                placeholder="Entrega sempre no fim do mês."
                className="campo h-[80px] resize-none py-2.5"
              />
              <p className="mt-1.5 text-xs text-texto-fraco">
                Visível só para a equipe, nunca na nota
              </p>
            </div>

            {ficha && (
              <LinhaInterruptor
                rotulo="Fornecedor ativo"
                dica="Inativos saem das listas sem sair da base — a nota de entrada é documento fiscal"
                ligado={rascunho.status === 'ativo'}
                aoTrocar={(valor) => alterar('status', valor ? 'ativo' : 'inativo')}
              />
            )}
          </div>
        </Cartao>
      </div>

      <div className="flex flex-col gap-4 xl:sticky xl:top-0">
        <div className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
          <h2 className="mb-3.5 font-titulo text-[15px] font-semibold text-tinta">Resumo</h2>
          <LinhaResumo rotulo="Tipo" valor={pf ? 'Pessoa física' : 'Pessoa jurídica'} />
          <LinhaResumo
            rotulo="Prazo de pagamento"
            valor={rascunho.prazoPagamentoDias ? `${rascunho.prazoPagamentoDias} dias` : 'A combinar'}
          />
          <LinhaResumo rotulo="Situação" valor={rascunho.status === 'ativo' ? 'Ativo' : 'Inativo'} />
        </div>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------- peças */

function Cartao({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string
  subtitulo: string
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <h2 className="font-titulo text-base font-semibold text-tinta">{titulo}</h2>
      <p className="mt-[2px] mb-5 text-[12.5px] text-texto-suave">{subtitulo}</p>
      {children}
    </section>
  )
}

/**
 * Interruptor de 42×24 com transição de 160ms — spec 01, seção "Controles".
 */
function LinhaInterruptor({
  rotulo,
  dica,
  ligado,
  aoTrocar,
}: {
  rotulo: string
  dica?: string
  ligado: boolean
  aoTrocar: (valor: boolean) => void
}) {
  return (
    <div className="flex items-center gap-3.5 border-t border-divisoria py-2.5 first:border-t-0">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-tinta">{rotulo}</div>
        {dica && <div className="mt-0.5 text-xs text-texto-suave">{dica}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={ligado}
        aria-label={rotulo}
        onClick={() => aoTrocar(!ligado)}
        className={`h-6 w-[42px] shrink-0 rounded-full p-[3px] transition-colors duration-150 ${
          ligado ? 'bg-marca' : 'bg-inativo'
        }`}
      >
        <span
          aria-hidden
          className="block h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform duration-150"
          style={{ transform: ligado ? 'translateX(18px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  )
}

function LinhaResumo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3 border-t border-divisoria py-2.5 text-[13px] first:border-t-0">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular text-right font-semibold text-tinta">{valor}</span>
    </div>
  )
}

/** Mesmo selo de `FormularioCliente.tsx` — ver o comentário lá para o porquê de ficar ao lado do rótulo. */
function IndicadorBusca() {
  return (
    <span role="status" className="flex items-center gap-1.5 text-[12px] font-medium text-marca">
      <Icone nome="sync" tamanho={13} className="animate-spin" />
      Buscando…
    </span>
  )
}
